// Relevé IPM du salarié (lui-même uniquement) — socle du 3 octobre 2026.
// Une seule présentation, partagée par « Mes prêts » et « Prêts SOGAS » : la dette IPM est la somme
// des restes à charge du salarié (ce que l'IPM n'a pas payé), diminuée des sommes déjà retenues.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { HeartPulse, FileText, Download } from 'lucide-react';
import { ouvrirPdf, messageErreurBlob } from '../../utils/ipmPdf';
import { Carte, Champ, Grille, Etat, EtatVide, Squelette, Bouton, BandeauInfo, formaterDate, NON_RENSEIGNE } from '../../design/composants';

const API = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
const fmtF = (n) => (n === null || n === undefined ? NON_RENSEIGNE : `${Number(n).toLocaleString('fr-FR')} FCFA`);

export default function MonIpm({ token, masquerSiVide = false }) {
  const [data, setData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [etat, setEtat] = useState('chargement');
  const [erreurPdf, setErreurPdf] = useState(null);
  useEffect(() => {
    if (!token) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/ipm/mon-solde`, h)
      .then((r) => { setData(r.data); setEtat('ok'); })
      .catch(() => setEtat('erreur'));
    axios.get(`${API}/ipm/mes-documents`, h).then((r) => setDocuments(r.data.lignes || [])).catch(() => setDocuments([]));
  }, [token]);
  const pdf = async (url, params) => { setErreurPdf(null); try { await ouvrirPdf(token, url, params); } catch (e) { setErreurPdf(await messageErreurBlob(e)); } };
  if (etat === 'chargement') return <Squelette hauteur={90} />;
  if (etat === 'erreur') return masquerSiVide ? null : <EtatVide titre="Informations IPM indisponibles" />;
  const d = data.dette;
  const rien = d.total_restes_a_charge === 0 && data.factures.length === 0;
  if (rien && masquerSiVide) return null;
  return (
    <Carte titre="Mon IPM" Icone={HeartPulse}>
      {rien ? <EtatVide titre="Aucune prise en charge IPM enregistrée" /> : (
        <>
          <Grille>
            <Champ libelle="Reste à ma charge (dette IPM)" valeur={fmtF(Math.max(0, d.solde))} />
            <Champ libelle="Restes à charge cumulés" valeur={fmtF(d.total_restes_a_charge)} />
            <Champ libelle="Déjà retenu sur mon salaire ou réglé" valeur={fmtF(d.total_recupere)} />
            {d.trop_percu > 0 && <Champ libelle="Trop-perçu à me restituer" valeur={fmtF(d.trop_percu)} />}
          </Grille>
          <p className="rh-cellule-secondaire" style={{ marginTop: 8 }}>
            L'IPM paie sa part directement au prestataire. Vous ne devez que votre reste à charge, retenu progressivement sur votre salaire.
          </p>
          {data.factures.length > 0 && (
            <>
              <h3 style={{ fontSize: 14, margin: '14px 0 6px' }}>Dernières prises en charge</h3>
              <ul className="rh-liste">
                {data.factures.slice(0, 5).map((f) => (
                  <li key={f.id}>
                    <span style={{ flex: 1 }}>
                      {formaterDate(f.date_soin)} · {f.beneficiaire_nom}{f.type_soin_libelle ? ` · ${f.type_soin_libelle}` : ''}
                      <div className="rh-cellule-secondaire">Facture {fmtF(f.montant_facture)} · payé par l'IPM {fmtF(f.part_ipm)} · reste à ma charge {fmtF(f.part_employe)}{f.motif_taux_zero ? ` · ${f.motif_taux_zero}` : ''}</div>
                    </span>
                    {f.statut === 'ANNULEE' && <Etat ton="neutre">Annulée</Etat>}
                  </li>
                ))}
              </ul>
            </>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <Bouton Icone={FileText} onClick={() => pdf('/ipm/mon-releve.pdf', { debut: `${new Date().getFullYear()}-01-01` })}>Mon relevé depuis janvier (provisoire)</Bouton>
          </div>
          {erreurPdf && <BandeauInfo ton="danger">{erreurPdf}</BandeauInfo>}
          {documents.length > 0 && (
            <>
              <h3 style={{ fontSize: 14, margin: '14px 0 6px' }}>Mes documents remis par l'IPM</h3>
              <ul className="rh-liste">
                {documents.slice(0, 10).map((d) => (
                  <li key={d.id}>
                    <span style={{ flex: 1 }}>Relevé du {formaterDate(d.periode_debut)} au {formaterDate(d.periode_fin)}<span className="rh-cellule-secondaire"> · réf. {d.reference} v{d.version}</span></span>
                    {d.statut !== 'ARRETE' && <Etat ton="neutre">Remplacé</Etat>}
                    <Bouton petit variante="discret" Icone={Download} onClick={() => pdf(`/ipm/documents/${d.id}/fichier`)}>Ouvrir</Bouton>
                  </li>
                ))}
              </ul>
            </>
          )}
          {data.mouvements.length > 0 && (
            <>
              <h3 style={{ fontSize: 14, margin: '14px 0 6px' }}>Mouvements de ma dette</h3>
              <ul className="rh-liste">
                {data.mouvements.slice(0, 8).map((m) => (
                  <li key={m.id}>
                    <span style={{ flex: 1 }}>{formaterDate(m.date_operation)} · {m.libelle_nature}{m.periode_retenue ? ` (paie ${m.periode_retenue})` : ''}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{m.sens > 0 ? '+' : '−'} {fmtF(m.montant)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </Carte>
  );
}
